import {
  GetTransferPaymentDetailsResponse,
  isDefined,
  Keys,
  Loadable,
  LoadableState,
  NbSelectItemOption,
  TransferPaymentItem,
  TransferPaymentTargetAccount,
} from '@eih-netbroker/common';
import { useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ErrorWidgetContent } from '../../../../shared/components/error-widget-content/ErrorWidgetContent';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { NbLoader } from '../../../../shared/components/loader/Loader';
import { ModalFooter } from '../../../../shared/components/modal/ModalFooter';
import { NbSelect } from '../../../../shared/components/select/Select';
import { CashActionNoSourceOrTargetAccountErrorFeedback } from '../common/CashActionNoSourceOrTargetAccountErrorFeedback';
import { CashActionParamEditor } from '../common/CashActionParamEditor';
import classes from './CashTransferParamEditorWrapper.module.scss';

type CashTransferParamEditorWrapperProps = {
  amount: string;
  transferDetails: LoadableState<GetTransferPaymentDetailsResponse>;
  numberInputError?: string;
  selectedSourceAccount?: TransferPaymentItem;
  selectedTargetAccount?: TransferPaymentTargetAccount;
  selectedCurrency: string;
  handleCurrencyChange: (currency: string) => void;
  currencyOptions: Array<NbSelectItemOption<string>>;
  sourceAccountOptions?: LoadableState<Array<NbSelectItemOption<TransferPaymentItem>>>;
  targetAccountOptions?: LoadableState<Array<NbSelectItemOption<TransferPaymentTargetAccount>>>;
  handleAmountChange: (value: string) => void;
  handleSourceAccountChange?: (value: string | TransferPaymentItem) => void;
  handleTargetAccountChange: (value: TransferPaymentTargetAccount | string) => void;
  handleSubmit: () => void;
  refreshTransferDetails: () => Promise<void>;
};

export const CashTransferParamEditorWrapper = ({
  amount,
  handleAmountChange,
  handleTargetAccountChange,
  selectedTargetAccount,
  targetAccountOptions,
  selectedCurrency,
  currencyOptions,
  handleCurrencyChange,
  numberInputError,
  handleSourceAccountChange,
  sourceAccountOptions,
  selectedSourceAccount,
  transferDetails,
  handleSubmit,
  refreshTransferDetails,
}: CashTransferParamEditorWrapperProps) => {
  const { translation } = useLocale();

  const noTargetAccountsAvailable = useMemo<boolean>(
    () =>
      isDefined(sourceAccountOptions) &&
      isDefined(selectedSourceAccount) &&
      isDefined(targetAccountOptions) &&
      sourceAccountOptions.isLoaded &&
      targetAccountOptions.isLoaded &&
      targetAccountOptions.length === 0,
    [selectedSourceAccount, sourceAccountOptions, targetAccountOptions],
  );

  if (transferDetails.isLoading || Loadable.isUninitialized(transferDetails)) {
    return (
      <div className={classes.content}>
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      </div>
    );
  }

  if (transferDetails.hasError) {
    return (
      <div className={classes.error500Container}>
        <ErrorWidgetContent onClick={refreshTransferDetails} />
      </div>
    );
  }

  // Handle received error codes
  if (transferDetails.isLoaded && transferDetails.messages.length !== 0) {
    if (
      transferDetails.messages.some(m => m.msg === Keys.CashAction.Errors.ATVEZETES_FORRAS_SZAMLA_NEM_VALID_IDOSZAK)
    ) {
      return (
        <div className={classes.errorContainer}>
          <FeedbackBox
            type="error"
            title={translation.cashAction.transfer.error.cannotLoadTransfer}
            className={classes.errorFeedbackBox}
          />
        </div>
      );
    }

    // No available source account found
    if (
      transferDetails.messages.some(m => m.msg === Keys.CashAction.Errors.NETBANKBROKER_ATVEZETES_NINCS_FORRAS_SZAMLA)
    ) {
      return (
        <div className={classes.errorContainer}>
          <CashActionNoSourceOrTargetAccountErrorFeedback
            cashActionType="transfer"
            selectedCurrency={selectedCurrency}
          />
        </div>
      );
    }

    // Unknown error code
    return (
      <div className={classes.error500Container}>
        <ErrorWidgetContent onClick={refreshTransferDetails} />
      </div>
    );
  }

  // Fallback state if we get no errors and the list is empty, invalid state
  if (transferDetails.isLoaded && transferDetails.transferPaymentItems.length === 0) {
    console.error('INVALID STATE: No error code has been received, yet source list is empty');
    return (
      <div className={classes.errorContainer}>
        <CashActionNoSourceOrTargetAccountErrorFeedback
          cashActionType="transfer"
          selectedCurrency={selectedCurrency}
        />
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.content}>
        <div className={classes.selectorBlock}>
          <p className={classes.selectorLabel}>{translation.cashAction.deposit.internal.currencyLabel}</p>
          <NbSelect<string>
            value={selectedCurrency}
            options={currencyOptions}
            onChange={event => handleCurrencyChange(event.target.value)}
            size="small"
            className={classes.currencySelect}
          />
        </div>
        <CashActionParamEditor<TransferPaymentItem, TransferPaymentTargetAccount>
          amount={amount}
          cashActionType="transfer"
          numberInputError={numberInputError}
          handleAmountChange={handleAmountChange}
          selectedTargetAccount={selectedTargetAccount}
          selectedSourceAccount={selectedSourceAccount}
          selectedCurrency={selectedCurrency}
          handleTargetAccountChange={handleTargetAccountChange}
          targetAccountOptions={targetAccountOptions}
          sourceAccountOptions={sourceAccountOptions}
          handleSourceAccountChange={handleSourceAccountChange}
          sourceAccountAmount={selectedSourceAccount?.amount}
          targetAccountAmount={selectedTargetAccount?.amount}
          setAmountToMaxLabel={translation.cashAction.transfer.transferAll}
          noTargetAccountsAvailable={noTargetAccountsAvailable}
        />
      </div>

      <ModalFooter
        right={{
          title: translation.cashAction.transfer.submitButtonText,
          action: handleSubmit,
          disabled:
            !isDefined(selectedSourceAccount) ||
            !isDefined(selectedTargetAccount) ||
            isDefined(numberInputError) ||
            amount === '',
        }}
      />
    </div>
  );
};
